"use client";

import { useFormatDate } from "@/lib/datetime-format-context";

import styles from "./date-tile.module.css";

/**
 * The one date tile, shared by every layout in the guide (`.dc` / `.dep-dc`).
 *
 * The month band is BRAND-FILLED with a white label, not a grey inset. That is
 * the whole character of the tile — it reads as a torn-off calendar leaf, and
 * it is what makes a column of departures scannable by date at a glance. A
 * grey band turns it into just another bordered box.
 *
 * A sold-out date drops the fill for the neutral one (`.dc-past`), so the
 * colour itself carries "this one is gone" before you read the pill.
 *
 * The month goes through the AGENCY's formatter, not
 * `toLocaleDateString(undefined, …)`. Two reasons, and the second is the
 * serious one:
 *
 *  - `undefined` means the runtime default, which ignores the locale and
 *    timezone the operator configured. On a French storefront the row read
 *    "sam. 14 mars 2026" from the agency formatter and the tile beside it said
 *    "MAR" from Node's default.
 *  - A departure row is SSR'd inside `DeparturesWithAvailability`. The server's
 *    default locale and the browser's need not agree, so the two renders could
 *    produce different text for the same date — a hydration mismatch.
 *
 * Its own file because the guide calls it shared and it is: lifting it out of
 * `departure-row.tsx` is also what puts that file back under the 250-line cap.
 */
export function DateTile({ date, dimmed }: { date: Date; dimmed: boolean }) {
  const formatDate = useFormatDate();
  const monthLabel = formatDate(date, {
    monthStyle: "short",
    omitDay: true,
    withYear: false,
  });
  return (
    <span aria-hidden className={styles.tile}>
      <span className={styles.month} data-dimmed={dimmed ? "" : undefined}>
        {monthLabel}
      </span>
      <span className={styles.day}>{date.getDate()}</span>
    </span>
  );
}
